import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { usePaginatedVocabularyList } from "../../hooks/usePaginatedVocabularyList";
import {
  useMemo,
  useState,
  useEffect,
  type ReactNode,
} from "react";
import useTranslation from "../../hooks/useTranslation";
import {
  computeVocabularyStage,
  formatVocabularyCreatedAt,
  formatRelativeToNow,
  streakStageKey,
} from "../../utils/vocabularyProgress";
import { TEXT_LANGUAGES } from "../../constants";
import {
  useMarkVocabularyAsForgotten,
  useMarkVocabularyAsLearned,
} from "../../hooks/useMarkVocabularyAsLearned";
import { useVocabularyStageCounts } from "../../hooks/useVocabularyStageCounts";
import {
  InformationCircleIcon,
  TrashIcon,
} from "@heroicons/react/24/outline";
import { HandThumbUpIcon, HandThumbDownIcon } from "@heroicons/react/24/solid";
import { VocabularyDto } from "../../hooks/usePaginatedVocabularyList";
import { useVocabularyDelete } from "../../hooks/useVocabularyDelete";
import { notifySnackbar } from "../../lib/notifySnackbar";
import { useTextStatistics } from "../../hooks/useTextStatistics";
import { useVocabularyStatistics } from "../../hooks/useVocabularyStatistics";
import {
  getVocabularyBaseWord,
  getVocabularyPrimaryWord,
  getVocabularyPrimaryTranslation,
  getVocabularyStructureDetails,
  getVocabularySurfaceWord,
  normalizeVocabularyTranslationDetails,
} from "../../utils/vocabularyTranslationDetails";
import { useLocalStorage } from "../../hooks/useLocalStorage";
import { formatBilingualExample } from "../../components/WordCard/wordCardSectionData";
import { highlightVocabularyWordInSentence } from "../../utils/highlightVocabularyWordInSentence";
import { VocabularyPronunciationButton } from "../../components/VocabularyPronunciationButton";
import { normalizeVocabularySourceSentence } from "../../utils/vocabularySourceSentenceCopy";
import {
  sortVocabularyProgress,
  type VocabularyProgressSortMode,
} from "../../utils/sortVocabularyProgress";
import { resolveVocabularyProgressLanguageFilters } from "../../utils/resolveVocabularyProgressLanguageFilters";
import { PatternUnlock } from "../../features/patternUnlocks/patternUnlocks";
import { PatternUnlockReward } from "../../features/patternUnlocks/PatternUnlockReward";
import { buildPatternCardFromVocabulary } from "../../features/patternUnlocks/patternUnlockDetection";
import { triggerPatternUnlocksForCard } from "../../features/patternUnlocks/patternUnlockTrigger";
import { PATTERN_UNLOCKS_MVP_VISIBLE } from "../../features/patternUnlocks/patternUnlockVisibility";
import {
  toggleAllVisibleVocabularySelection,
  toggleVocabularySelection,
} from "../../utils/vocabularyBulkSelection";

const VOCABULARY_REVIEWED_EVENT = "VOCABULARY_REVIEWED";
const LANGUAGE_FILTER_STORAGE_KEY = "vocabulary-progress-language-filters-v1";

type VocabularyStageWithAll =
  | "ALL"
  | "NEW"
  | "LEARNING"
  | "REVIEWING"
  | "MASTERED"
  | "DUE";

type DerivedVocabulary = VocabularyDto & {
  stage: Exclude<VocabularyStageWithAll, "ALL">;
  nextReviewDate: Date | null;
  isDue: boolean;
};

type DeleteSelectionState = {
  ids: number[];
  mode: "single" | "multi";
};

function normalizeSearchText(value: string | null | undefined): string {
  return (value ?? "")
    .toLocaleLowerCase()
    .replace(/[’`´]/g, "'")
    .replace(/[‐‑‒–—]/g, "-")
    .replace(/\s+/g, " ")
    .trim();
}

const STAGE_META: {
  id: Exclude<VocabularyStageWithAll, "ALL">;
  labelKey: string;
  descriptionKey: string;
  accent: string;
  intervalKey?: string;
}[] = [
  {
    id: "DUE",
    labelKey: "vocabulary_progress_stage_due",
    descriptionKey: "vocabulary_progress_stage_due_description",
    accent: "bg-rose-50 text-rose-700 border-rose-200",
  },
  {
    id: "NEW",
    labelKey: "vocabulary_progress_stage_new",
    descriptionKey: "vocabulary_progress_stage_new_description",
    accent: "bg-blue-50 text-blue-700 border-blue-200",
  },
  {
    id: "LEARNING",
    labelKey: "vocabulary_progress_stage_learning",
    descriptionKey: "vocabulary_progress_stage_learning_description",
    accent: "bg-amber-50 text-amber-700 border-amber-200",
    intervalKey: "vocabulary_progress_stage_learning_interval",
  },
  {
    id: "REVIEWING",
    labelKey: "vocabulary_progress_stage_reviewing",
    descriptionKey: "vocabulary_progress_stage_reviewing_description",
    accent: "bg-indigo-50 text-indigo-700 border-indigo-200",
    intervalKey: "vocabulary_progress_stage_reviewing_interval",
  },
  {
    id: "MASTERED",
    labelKey: "vocabulary_progress_stage_mastered",
    descriptionKey: "vocabulary_progress_stage_mastered_description",
    accent: "bg-emerald-50 text-emerald-700 border-emerald-200",
    intervalKey: "vocabulary_progress_stage_mastered_interval",
  },
];

const SRS_SCHEDULE = [
  {
    streakLabel: "0",
    translationKey: "vocabulary_progress_srs_schedule_0",
  },
  {
    streakLabel: "1",
    translationKey: "vocabulary_progress_srs_schedule_1",
  },
  {
    streakLabel: "2",
    translationKey: "vocabulary_progress_srs_schedule_2",
  },
  {
    streakLabel: "3",
    translationKey: "vocabulary_progress_srs_schedule_3",
  },
  {
    streakLabel: "4",
    translationKey: "vocabulary_progress_srs_schedule_4",
  },
  {
    streakLabel: "5+",
    translationKey: "vocabulary_progress_srs_schedule_5",
  },
];

export const Route = createFileRoute("/app/vocabulary_progress")({
  validateSearch: (search: Record<string, unknown>) => {
    const parsedGeneratedTextId =
      typeof search.generatedTextId === "number"
        ? search.generatedTextId
        : typeof search.generatedTextId === "string"
          ? Number(search.generatedTextId)
          : undefined;

    return {
      tab: typeof search.tab === "string" ? search.tab : undefined,
      generatedTextId: Number.isFinite(parsedGeneratedTextId)
        ? parsedGeneratedTextId
        : undefined,
    };
  },
  component: VocabularyProgressPage,
});

function VocabularyProgressPage() {
  const { translate, language } = useTranslation();
  const { tab, generatedTextId } = Route.useSearch();
  const navigate = useNavigate();
  const [languageFilters, setLanguageFilters] = useLocalStorage<string[]>(
    LANGUAGE_FILTER_STORAGE_KEY,
    [],
  );
  const [stageFilter, setStageFilter] =
    useState<VocabularyStageWithAll>("ALL");
  const [searchTerm, setSearchTerm] = useState("");
  const [sortMode, setSortMode] =
    useState<VocabularyProgressSortMode>("REVIEW_PRIORITY");
  const [activeWorkspaceLanguage, setActiveWorkspaceLanguage] =
    useState<string>("ALL_SELECTED");
  const [workspaceLanguagePickerOpen, setWorkspaceLanguagePickerOpen] =
    useState(false);
  const [showMoreGrammar, setShowMoreGrammar] = useLocalStorage<boolean>(
    "vocabulary-more-grammar-toggle-v3",
    true,
  );
  const [revealedTranslationIds, setRevealedTranslationIds] = useState<number[]>(
    [],
  );
  const [vocabularyToDelete, setVocabularyToDelete] =
    useState<DeleteSelectionState | null>(null);
  const [selectedVocabularyIds, setSelectedVocabularyIds] = useState<number[]>([]);
  const [queuedUnlocks, setQueuedUnlocks] = useState<PatternUnlock[]>([]);
  const [activeUnlock, setActiveUnlock] = useState<PatternUnlock | null>(null);
  const initialOriginTab: "ALL" | "PHOTO" =
    tab === "photo" ? "PHOTO" : "ALL";
  const [originTab, setOriginTab] = useState<"ALL" | "PHOTO">(
    initialOriginTab,
  );
  const deleteVocabulary = useVocabularyDelete();
  const textStatistics = useTextStatistics();
  const vocabularyStatistics = useVocabularyStatistics();
  const numberLocale = language === "GERMAN" ? "de-DE" : "en-US";
  const formatNumber = (value: number) =>
    new Intl.NumberFormat(numberLocale).format(value);
  const updateTabSearch = (value: "ALL" | "PHOTO") => {
    navigate({
      to: "/app/vocabulary_progress",
      search: {
        tab: value === "PHOTO" ? "photo" : undefined,
        generatedTextId,
      },
      replace: true,
    });
  };

  // Empty selection means "all languages". Multi-select supports parallel learning
  // tracks like French + Spanish without coupling to global app language-space state.
  const effectiveLanguageFilters = useMemo(
    () =>
      resolveVocabularyProgressLanguageFilters({
        selectedLanguages: languageFilters,
        availableLanguageIds: TEXT_LANGUAGES.map((lang) => lang.id),
      }),
    [languageFilters],
  );

  const paginatedVocabularyList = usePaginatedVocabularyList({
    generated_text_id: generatedTextId,
    origins: originTab === "PHOTO" ? ["PHOTO_IMPORT"] : undefined,
    word_languages:
      generatedTextId === undefined && effectiveLanguageFilters.length > 0
        ? effectiveLanguageFilters
        : undefined,
  });
  const stageCountsQuery = useVocabularyStageCounts({
    generatedTextId,
    languages:
      generatedTextId === undefined && effectiveLanguageFilters.length > 0
        ? effectiveLanguageFilters
        : undefined,
    origins: originTab === "PHOTO" ? ["PHOTO_IMPORT"] : undefined,
    maxStreak: 10000,
    textAgeInDays: 2000 * 365,
  });
  const { hasNextPage, isFetchingNextPage, fetchNextPage } =
    paginatedVocabularyList;
  const vocabularyList = useMemo(() => {
    if (!paginatedVocabularyList.isSuccess) {
      return [];
    }
    return paginatedVocabularyList.data.pages.flatMap((page) => page.results);
  }, [paginatedVocabularyList.data, paginatedVocabularyList.isSuccess]);

  useEffect(() => {
    const nextTab: "ALL" | "PHOTO" = tab === "photo" ? "PHOTO" : "ALL";
    if (originTab !== nextTab) {
      setOriginTab(nextTab);
    }
  }, [originTab, tab]);

  useEffect(() => {
    const handler = () => {
      void paginatedVocabularyList.refetch();
      void stageCountsQuery.refetch();
    };
    document.addEventListener(VOCABULARY_REVIEWED_EVENT, handler);
    return () => {
      document.removeEventListener(VOCABULARY_REVIEWED_EVENT, handler);
    };
  }, [paginatedVocabularyList, stageCountsQuery]);

  const derivedVocabularyList: DerivedVocabulary[] = useMemo(() => {
    return vocabularyList.map((vocabulary) => {
      const { stage, nextReviewDate, isDue } = computeVocabularyStage({
        streak: vocabulary.winning_streak ?? 0,
        lastTested: vocabulary.last_tested,
      });
      return {
        ...vocabulary,
        stage,
        nextReviewDate,
        isDue,
      };
    });
  }, [vocabularyList]);

  const languageLabelById = useMemo(
    () =>
      Object.fromEntries(
        TEXT_LANGUAGES.map((lang) => [lang.id, translate(lang.prettyTranslationKey)]),
      ) as Record<string, string>,
    [translate],
  );

  const availableLanguageCounts = useMemo(() => {
    return derivedVocabularyList.reduce<Record<string, number>>((acc, item) => {
      acc[item.word_language] = (acc[item.word_language] ?? 0) + 1;
      return acc;
    }, {});
  }, [derivedVocabularyList]);

  const selectedWorkspaceLanguages = useMemo(() => {
    if (effectiveLanguageFilters.length > 0) {
      return effectiveLanguageFilters;
    }
    return TEXT_LANGUAGES.map((lang) => lang.id);
  }, [effectiveLanguageFilters]);

  useEffect(() => {
    if (activeWorkspaceLanguage === "ALL_SELECTED") {
      return;
    }
    if (!selectedWorkspaceLanguages.includes(activeWorkspaceLanguage)) {
      setActiveWorkspaceLanguage("ALL_SELECTED");
    }
  }, [activeWorkspaceLanguage, selectedWorkspaceLanguages]);

  useEffect(() => {
    setActiveWorkspaceLanguage("ALL_SELECTED");
  }, [effectiveLanguageFilters]);

  const filteredVocabulary = useMemo(() => {
    return derivedVocabularyList.filter((vocabulary) => {
      const matchesLanguage =
        effectiveLanguageFilters.length === 0 ||
        effectiveLanguageFilters.includes(vocabulary.word_language);
      const matchesStage =
        stageFilter === "ALL" || vocabulary.stage === stageFilter;
      const normalizedSearch = normalizeSearchText(searchTerm);
      const matchesSearch =
        normalizedSearch.length === 0 ||
        normalizeSearchText(vocabulary.word).includes(normalizedSearch) ||
        normalizeSearchText(vocabulary.translation).includes(normalizedSearch) ||
        normalizeSearchText(vocabulary.sentence).includes(normalizedSearch) ||
        normalizeSearchText(
          vocabulary.translation_details?.source_surface_form ?? "",
        ).includes(normalizedSearch);

      return matchesLanguage && matchesStage && matchesSearch;
    });
  }, [
    derivedVocabularyList,
    effectiveLanguageFilters,
    stageFilter,
    searchTerm,
  ]);

  const workspaceVocabulary = useMemo(() => {
    if (activeWorkspaceLanguage === "ALL_SELECTED") {
      return filteredVocabulary;
    }
    return filteredVocabulary.filter(
      (vocabulary) => vocabulary.word_language === activeWorkspaceLanguage,
    );
  }, [activeWorkspaceLanguage, filteredVocabulary]);

  const sortedVocabulary = useMemo(() => {
    return sortVocabularyProgress(workspaceVocabulary, sortMode);
  }, [sortMode, workspaceVocabulary]);
  const visibleVocabularyCount = sortedVocabulary.length;
  const visibleVocabularyIds = useMemo(
    () => sortedVocabulary.map((entry) => entry.id),
    [sortedVocabulary],
  );
  const selectedVisibleVocabularyCount = useMemo(
    () =>
      visibleVocabularyIds.filter((id) => selectedVocabularyIds.includes(id))
        .length,
    [selectedVocabularyIds, visibleVocabularyIds],
  );
  const isAllVisibleSelected =
    visibleVocabularyIds.length > 0 &&
    selectedVisibleVocabularyCount === visibleVocabularyIds.length;
  const totalVocabularyCount = useMemo(() => {
    if (!paginatedVocabularyList.isSuccess) {
      return 0;
    }
    return paginatedVocabularyList.data.pages[0]?.count ?? vocabularyList.length;
  }, [paginatedVocabularyList.data, paginatedVocabularyList.isSuccess, vocabularyList.length]);
  const workspaceVisibleTotalCount = useMemo(
    () =>
      Object.values(availableLanguageCounts).reduce(
        (sum, value) => sum + value,
        0,
      ),
    [availableLanguageCounts],
  );
  const selectedLanguageSummary = useMemo(() => {
    if (effectiveLanguageFilters.length === 0) {
      return translate("spaced_preview_language_all");
    }
    const labels = effectiveLanguageFilters.map(
      (languageId) => languageLabelById[languageId] ?? languageId,
    );
    return `${labels.join(", ")} (${effectiveLanguageFilters.length})`;
  }, [
    effectiveLanguageFilters,
    languageLabelById,
    translate,
    workspaceVisibleTotalCount,
  ]);

  const stageStats = useMemo(() => {
    if (activeWorkspaceLanguage !== "ALL_SELECTED") {
      return workspaceVocabulary.reduce<Record<string, number>>((acc, vocab) => {
        acc[vocab.stage] = (acc[vocab.stage] ?? 0) + 1;
        return acc;
      }, {});
    }
    const serverCounts = stageCountsQuery.data;
    if (serverCounts) {
      return serverCounts;
    }
    return workspaceVocabulary.reduce<Record<string, number>>((acc, vocab) => {
      acc[vocab.stage] = (acc[vocab.stage] ?? 0) + 1;
      return acc;
    }, {});
  }, [activeWorkspaceLanguage, stageCountsQuery.data, workspaceVocabulary]);

  useEffect(() => {
    const visibleSet = new Set(visibleVocabularyIds);
    setSelectedVocabularyIds((prev) => prev.filter((id) => visibleSet.has(id)));
  }, [visibleVocabularyIds]);

  const handleDeleteRequest = (vocabulary: DerivedVocabulary) => {
    setVocabularyToDelete({ ids: [vocabulary.id], mode: "single" });
  };
  const handleToggleVocabularySelection = (vocabularyId: number) => {
    setSelectedVocabularyIds((prev) =>
      toggleVocabularySelection(prev, vocabularyId),
    );
  };
  const handleToggleSelectAllVisible = () => {
    setSelectedVocabularyIds((prev) =>
      toggleAllVisibleVocabularySelection({
        selectedIds: prev,
        visibleIds: visibleVocabularyIds,
      }),
    );
  };
  const handleDeleteSelectedRequest = () => {
    if (selectedVocabularyIds.length === 0) {
      return;
    }
    setVocabularyToDelete({ ids: selectedVocabularyIds, mode: "multi" });
  };
  const handlePatternSignal = (signal: {
    viaCompletion: boolean;
    viaStructure: boolean;
    patternCard: ReturnType<typeof buildPatternCardFromVocabulary>;
  }) => {
    const unlocked = triggerPatternUnlocksForCard(signal.patternCard, {
      viaCompletion: signal.viaCompletion,
      viaStructure: signal.viaStructure,
    });
    if (unlocked.length === 0) {
      return;
    }
    setQueuedUnlocks((prev) => {
      if (!activeUnlock) {
        const [first, ...rest] = unlocked;
        setActiveUnlock(first);
        return [...prev, ...rest];
      }
      return [...prev, ...unlocked];
    });
  };
  const isTranslationRevealed = (vocabularyId: number) =>
    revealedTranslationIds.includes(vocabularyId);
  const toggleTranslationReveal = (vocabularyId: number) => {
    setRevealedTranslationIds((prev) =>
      prev.includes(vocabularyId)
        ? prev.filter((id) => id !== vocabularyId)
        : [...prev, vocabularyId],
    );
  };

  const handleCancelDelete = () => {
    if (deleteVocabulary.isPending) {
      return;
    }
    setVocabularyToDelete(null);
  };

  const handleConfirmDelete = async () => {
    if (!vocabularyToDelete) {
      return;
    }
    try {
      await Promise.all(
        vocabularyToDelete.ids.map((vocabularyId) =>
          deleteVocabulary.mutateAsync({ vocabularyId }),
        ),
      );
      if (vocabularyToDelete.mode === "single") {
        const deletedVocabulary = sortedVocabulary.find(
          (item) => item.id === vocabularyToDelete.ids[0],
        );
        notifySnackbar({
          message: translate("vocabulary_progress_delete_success", {
            word: deletedVocabulary?.word ?? "",
          }),
        });
      } else {
        notifySnackbar({
          message: translate("vocabulary_progress_delete_multi_success", {
            count: vocabularyToDelete.ids.length,
          }),
        });
      }
      setSelectedVocabularyIds((prev) =>
        prev.filter((id) => !vocabularyToDelete.ids.includes(id)),
      );
      setVocabularyToDelete(null);
    } catch (error) {
      console.error(error);
      notifySnackbar({
        message: translate("vocabulary_progress_delete_error"),
      });
    }
  };

  return (
    <div className="space-y-5 px-3 py-4 md:space-y-6 md:px-4 md:py-6">
      <header className="rounded-2xl border border-slate-200 bg-white p-4 md:p-5">
        <div className="space-y-2">
          <p className="text-sm font-semibold uppercase tracking-wide text-slate-500">
            {translate("vocabulary_progress_label")}
          </p>
          <h1 className="text-2xl font-bold text-slate-900 md:text-3xl">
            {translate("vocabulary_progress_title")}
          </h1>
          <p className="max-w-3xl text-sm text-slate-600 md:text-base">
            {translate("vocabulary_progress_description")}
          </p>
        </div>
        <div className="mt-4 -mx-1 overflow-x-auto px-1 pb-1">
          <div className="flex w-max gap-2 sm:w-auto sm:flex-wrap">
            <button
              type="button"
              onClick={() => {
                setOriginTab("ALL");
                updateTabSearch("ALL");
              }}
              className={`rounded-full border px-4 py-2 text-base font-semibold transition md:text-sm ${
                originTab === "ALL"
                  ? "border-slate-900 bg-slate-900 text-white"
                  : "border-slate-200 text-slate-600 hover:border-slate-400"
              }`}
            >
              {translate("vocabulary_tab_all")}
            </button>
            <button
              type="button"
              onClick={() => {
                setOriginTab("PHOTO");
                updateTabSearch("PHOTO");
              }}
              className={`rounded-full border px-4 py-2 text-base font-semibold transition md:text-sm ${
                originTab === "PHOTO"
                  ? "border-slate-900 bg-slate-900 text-white"
                  : "border-slate-200 text-slate-600 hover:border-slate-400"
              }`}
            >
              {translate("vocabulary_tab_photo")}
            </button>
          </div>
        </div>
      </header>

      <section className="space-y-4">
        <div className="rounded-2xl border border-slate-200 bg-white p-4">
          <p className="pb-4 text-lg font-semibold text-slate-900">
            {translate("statistics_header_vocabulary")}
          </p>
          <div className="grid gap-3 md:grid-cols-3">
            {[
              {
                key: "total_words_added",
                text: translate("statistics_total_words_added"),
                value: vocabularyStatistics.data?.total_created ?? 0,
              },
              {
                key: "total_words_added_last_seven_days",
                text: translate("statistics_total_words_added_last_seven_days"),
                value: vocabularyStatistics.data?.last_week_created ?? 0,
              },
              {
                key: "total_words_added_last_four_weeks",
                text: translate("statistics_total_words_added_last_four_weeks"),
                value: vocabularyStatistics.data?.last_four_weeks_created ?? 0,
              },
            ].map((item) => (
              <div
                key={item.key}
                className="rounded-xl border border-slate-200 bg-slate-50 p-4"
              >
                <p className="pb-2 text-3xl font-bold text-slate-900">
                  {formatNumber(item.value)}
                </p>
                <p className="text-sm font-medium text-slate-600">{item.text}</p>
              </div>
            ))}
          </div>
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white p-4">
          <p className="pb-4 text-lg font-semibold text-slate-900">
            {translate("statistics_header_texts")}
          </p>
          <div className="grid gap-3 md:grid-cols-3">
            {[
              {
                key: "total_texts_created",
                text: translate("statistics_total_texts_created"),
                value: textStatistics.data?.total_created_texts ?? 0,
              },
              {
                key: "total_created_texts_last_week",
                text: translate("statistics_total_reated_texts_last_week"),
                value: textStatistics.data?.last_week_created_texts ?? 0,
              },
              {
                key: "total_word_count",
                text: translate("statistics_total_word_count"),
                value: textStatistics.data?.total_created_texts_length ?? 0,
              },
            ].map((item) => (
              <div
                key={item.key}
                className="rounded-xl border border-slate-200 bg-slate-50 p-4"
              >
                <p className="pb-2 text-3xl font-bold text-slate-900">
                  {formatNumber(item.value)}
                </p>
                <p className="text-sm font-medium text-slate-600">{item.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="grid grid-cols-1 gap-3 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-5">
        {STAGE_META.map((stage) => {
          const description = translate(stage.descriptionKey);
          const interval = stage.intervalKey
            ? translate(stage.intervalKey)
            : null;

          return (
            <button
              key={stage.id}
              type="button"
              onClick={() =>
                setStageFilter((current) =>
                  current === stage.id ? "ALL" : stage.id,
                )
              }
              className={`flex w-full flex-col items-start rounded-2xl border p-4 text-left transition-all ${stage.accent} ${
                stageFilter === stage.id
                  ? "ring-2 ring-offset-2 ring-slate-900"
                  : ""
              }`}
            >
              <p className="text-sm font-semibold uppercase tracking-wide">
                {translate(stage.labelKey)}
              </p>
              <p className="text-2xl font-bold md:text-3xl">
                {stageStats[stage.id] ?? 0}
              </p>
              <p
                className="mt-2 text-xs text-slate-700 text-pretty leading-snug"
                title={description}
              >
                {description}
              </p>
              {interval && (
                <p
                  className="text-[11px] text-slate-600 text-pretty leading-snug"
                  title={interval}
                >
                  {interval}
                </p>
              )}
            </button>
          );
        })}
      </section>

      <SrsInfoPanel translate={translate} />

      <section className="rounded-2xl border border-slate-200 bg-white p-4 md:p-5">
        <div className="space-y-1">
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
            {translate("vocabulary_progress_workspace_label")}
          </p>
          <h2 className="text-lg font-semibold text-slate-900">
            {translate("vocabulary_progress_workspace_title")}
          </h2>
          <p className="text-sm text-slate-600">
            {translate("vocabulary_progress_workspace_description")}
          </p>
        </div>
        <div className="mt-3 space-y-3">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-sm font-semibold text-slate-700">
              {translate("vocabulary_progress_workspace_selection_label")}
            </p>
            <button
              type="button"
              onClick={() => setWorkspaceLanguagePickerOpen((prev) => !prev)}
              className="rounded-full border border-slate-300 px-4 py-2 text-base font-semibold text-slate-700 md:text-sm"
            >
              {workspaceLanguagePickerOpen
                ? translate("vocabulary_progress_workspace_picker_hide")
                : translate("vocabulary_progress_workspace_picker_show")}
            </button>
          </div>
          <p className="text-sm text-slate-600">
            {translate("vocabulary_progress_workspace_active_label")}{" "}
            <span className="font-semibold text-slate-800">
              {selectedLanguageSummary}
            </span>
          </p>
          {workspaceLanguagePickerOpen && (
            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-3">
              <label className="mb-2 flex items-center gap-2 text-sm font-medium text-slate-700">
                <input
                  type="checkbox"
                  className="h-5 w-5 rounded border-slate-300"
                  checked={effectiveLanguageFilters.length === 0}
                  onChange={() => setLanguageFilters([])}
                />
                {translate("spaced_preview_language_all")}
              </label>
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                {TEXT_LANGUAGES.map((lang) => {
                  const isSelected = effectiveLanguageFilters.includes(lang.id);
                  return (
                    <label
                      key={lang.id}
                      className="flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700"
                    >
                      <input
                        type="checkbox"
                        className="h-5 w-5 rounded border-slate-300"
                        checked={isSelected}
                        onChange={() => {
                          if (effectiveLanguageFilters.length === 0) {
                            setLanguageFilters([lang.id]);
                            return;
                          }
                          if (isSelected) {
                            setLanguageFilters(
                              effectiveLanguageFilters.filter((value) => value !== lang.id),
                            );
                            return;
                          }
                          setLanguageFilters([...effectiveLanguageFilters, lang.id]);
                        }}
                      />
                      <span>
                        {translate(lang.prettyTranslationKey)}
                      </span>
                    </label>
                  );
                })}
              </div>
            </div>
          )}
        </div>
        <div className="mt-4 space-y-2">
          <p className="text-sm font-semibold text-slate-700">
            {translate("vocabulary_progress_workspace_focus_label")}
          </p>
          <p className="text-xs text-slate-500">
            {translate("vocabulary_progress_workspace_focus_hint", {
              count:
                effectiveLanguageFilters.length === 0
                  ? TEXT_LANGUAGES.length
                  : effectiveLanguageFilters.length,
            })}
          </p>
          <select
            value={activeWorkspaceLanguage}
            onChange={(event) => setActiveWorkspaceLanguage(event.target.value)}
            className="w-full rounded-full border border-slate-200 bg-white px-4 py-2 text-base md:text-sm"
          >
            <option value="ALL_SELECTED">
              {translate("vocabulary_progress_workspace_view_all_selected")}
            </option>
            {selectedWorkspaceLanguages.map((languageId) => (
              <option key={languageId} value={languageId}>
                {languageLabelById[languageId] ?? languageId}
              </option>
            ))}
          </select>
        </div>
        <p className="mt-3 text-xs text-slate-500">
          {translate("vocabulary_progress_workspace_total_hint", {
            total: formatNumber(totalVocabularyCount),
          })}
        </p>
      </section>

      <section className="grid gap-4 rounded-2xl border border-slate-200 bg-white p-4 md:grid-cols-2 md:p-5 xl:grid-cols-3">
        <div className="flex w-full flex-col gap-2">
          <span className="text-xs font-semibold uppercase text-slate-500">
            {translate("vocabulary_progress_filters_stage")}
          </span>
          <div className="flex flex-wrap gap-2">
            <FilterChip
              label={translate("vocabulary_progress_filters_stage_all")}
              active={stageFilter === "ALL"}
              onClick={() => setStageFilter("ALL")}
            />
            {STAGE_META.map((stage) => (
              <FilterChip
                key={stage.id}
                label={translate(stage.labelKey)}
                active={stageFilter === stage.id}
                onClick={() => setStageFilter(stage.id)}
              />
            ))}
          </div>
        </div>
        <div className="flex flex-col gap-2 w-full lg:w-1/3">
          <span className="text-xs font-semibold uppercase text-slate-500">
            {translate("vocabulary_progress_filters_search")}
          </span>
          <input
            type="search"
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
            placeholder={translate(
              "vocabulary_progress_filters_search_placeholder",
            )}
            className="w-full rounded-full border border-slate-200 px-4 py-2 text-base focus:border-slate-900 focus:outline-none focus:ring-2 focus:ring-slate-100"
          />
        </div>
        <div className="flex w-full flex-col gap-2">
          <span className="text-xs font-semibold uppercase text-slate-500">
            {translate("vocabulary_progress_filters_sort")}
          </span>
          <select
            value={sortMode}
            onChange={(event) =>
              setSortMode(event.target.value as VocabularyProgressSortMode)
            }
            className="w-full rounded-full border border-slate-200 bg-white px-4 py-2 text-base focus:border-slate-900 focus:outline-none focus:ring-2 focus:ring-slate-100"
          >
            <option value="REVIEW_PRIORITY">
              {translate("vocabulary_progress_sort_review_priority")}
            </option>
            <option value="WORD_A_Z">
              {translate("vocabulary_progress_sort_word_az")}
            </option>
            <option value="WORD_Z_A">
              {translate("vocabulary_progress_sort_word_za")}
            </option>
            <option value="NEWEST_ADDED">
              {translate("vocabulary_progress_sort_newest")}
            </option>
            <option value="OLDEST_ADDED">
              {translate("vocabulary_progress_sort_oldest")}
            </option>
            <option value="MOST_PRACTICED">
              {translate("vocabulary_progress_sort_most_practiced")}
            </option>
          </select>
        </div>
      </section>

      <section>
        <div className="mb-3 rounded-2xl border border-slate-200 bg-white p-4 md:p-5">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
            <h2 className="text-xl font-semibold text-slate-900">
              {translate("vocabulary_progress_table_title")}
            </h2>
            <div className="grid grid-cols-1 gap-1 text-sm text-slate-500 sm:text-right">
              <p>
                {translate("vocabulary_progress_table_showing")}{" "}
                <span className="font-semibold text-slate-700">
                  {formatNumber(totalVocabularyCount)}
                </span>
              </p>
              <p>
                {translate("vocabulary_progress_workspace_selection_label")}:{" "}
                <span className="font-semibold text-slate-700">
                  {effectiveLanguageFilters.length === 0
                    ? `${translate("spaced_preview_language_all")} (${workspaceVisibleTotalCount})`
                    : `${effectiveLanguageFilters.length}`}
                </span>
              </p>
              <p>
                {translate("vocabulary_progress_workspace_total_hint", {
                  total: formatNumber(totalVocabularyCount),
                })}
              </p>
            </div>
          </div>
          <div className="mt-4 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <label className="flex items-center gap-2 text-sm text-slate-700">
              <input
                type="checkbox"
                className="h-5 w-5 rounded border-slate-300"
                checked={isAllVisibleSelected}
                onChange={handleToggleSelectAllVisible}
              />
              {translate("vocabulary_progress_select_all_visible")}
            </label>
            <button
              type="button"
              onClick={handleDeleteSelectedRequest}
              disabled={selectedVocabularyIds.length === 0 || deleteVocabulary.isPending}
              className="inline-flex items-center justify-center gap-2 rounded-full border border-rose-200 px-4 py-2 text-sm font-semibold text-rose-700 transition hover:border-rose-300 hover:bg-rose-50 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <TrashIcon className="h-4 w-4" />
              {translate("vocabulary_progress_delete_selected", {
                count: selectedVocabularyIds.length,
              })}
            </button>
          </div>
        </div>

        {visibleVocabularyCount === 0 ? (
          <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 p-6 text-center text-slate-600">
            {translate("vocabulary_progress_empty_state")}
          </div>
        ) : (
          <>
            <div className="hidden overflow-x-auto rounded-2xl border border-slate-200 bg-white md:block">
              <table className="min-w-full divide-y divide-slate-100">
                <thead className="bg-slate-50">
                  <tr>
                    <TableHeader>{translate("vocabulary_progress_table_select")}</TableHeader>
                    <TableHeader>{translate("vocabulary_progress_table_word")}</TableHeader>
                    <TableHeader>{translate("vocabulary_progress_table_translation")}</TableHeader>
                    <TableHeader>{translate("vocabulary_progress_table_stage")}</TableHeader>
                    <TableHeader>{translate("vocabulary_progress_table_streak")}</TableHeader>
                    <TableHeader>{translate("vocabulary_progress_table_last_practiced")}</TableHeader>
                    <TableHeader>{translate("vocabulary_progress_table_next_review")}</TableHeader>
                    <TableHeader>{translate("vocabulary_progress_table_actions")}</TableHeader>
                  </tr>
                </thead>
              <tbody className="divide-y divide-slate-100 text-sm">
                  {sortedVocabulary.map((vocabulary) => (
                    <VocabularyTableRow
                      key={vocabulary.id}
                      vocabulary={vocabulary}
                      translate={translate}
                      translationRevealed={isTranslationRevealed(vocabulary.id)}
                      onToggleTranslationReveal={toggleTranslationReveal}
                      showMoreGrammar={showMoreGrammar}
                      setShowMoreGrammar={setShowMoreGrammar}
                      onDeleteRequest={handleDeleteRequest}
                      selected={selectedVocabularyIds.includes(vocabulary.id)}
                      onToggleSelect={handleToggleVocabularySelection}
                      onPatternSignal={handlePatternSignal}
                    />
                  ))}
                </tbody>
              </table>
            </div>

            <div className="space-y-3 md:hidden">
              {sortedVocabulary.map((vocabulary) => (
                <VocabularyCardRow
                  key={vocabulary.id}
                  vocabulary={vocabulary}
                  translate={translate}
                  translationRevealed={isTranslationRevealed(vocabulary.id)}
                  onToggleTranslationReveal={toggleTranslationReveal}
                  showMoreGrammar={showMoreGrammar}
                  setShowMoreGrammar={setShowMoreGrammar}
                  onDeleteRequest={handleDeleteRequest}
                  selected={selectedVocabularyIds.includes(vocabulary.id)}
                  onToggleSelect={handleToggleVocabularySelection}
                  onPatternSignal={handlePatternSignal}
                />
              ))}
            </div>
          </>
        )}
      </section>

      {isFetchingNextPage && (
        <p className="text-center text-sm text-slate-500">
          {translate("vocabulary_progress_loading_more")}
        </p>
      )}
      {hasNextPage && !isFetchingNextPage && (
        <div className="flex justify-center">
          <button
            type="button"
            onClick={() => {
              void fetchNextPage();
            }}
            className="rounded-full border border-slate-300 bg-white px-5 py-2 text-sm font-semibold text-slate-700 transition hover:border-slate-500"
          >
            {translate("vocabulary_progress_load_more")}
          </button>
        </div>
      )}
      {vocabularyToDelete && (
        <DeleteVocabularyDialog
          vocabularies={sortedVocabulary.filter((entry) =>
            vocabularyToDelete.ids.includes(entry.id),
          )}
          mode={vocabularyToDelete.mode}
          translate={translate}
          isLoading={deleteVocabulary.isPending}
          onCancel={handleCancelDelete}
          onConfirm={handleConfirmDelete}
        />
      )}
      {PATTERN_UNLOCKS_MVP_VISIBLE && activeUnlock && (
        <PatternUnlockReward
          reward={activeUnlock}
          title={translate("pattern_unlocked_title")}
          dismissLabel={translate("pattern_unlock_dismiss")}
          insightsLabel={translate("pattern_unlock_view_insights")}
          onDismiss={() => {
            setActiveUnlock(null);
            setQueuedUnlocks((prev) => {
              if (prev.length === 0) {
                return prev;
              }
              const [next, ...rest] = prev;
              setActiveUnlock(next);
              return rest;
            });
          }}
        />
      )}
      <p className="sr-only">{queuedUnlocks.length}</p>
    </div>
  );
}

function VocabularyTableRow({
  vocabulary,
  translate,
  translationRevealed,
  onToggleTranslationReveal,
  showMoreGrammar,
  setShowMoreGrammar,
  onDeleteRequest,
  selected,
  onToggleSelect,
  onPatternSignal,
}: {
  vocabulary: DerivedVocabulary;
  translate: ReturnType<typeof useTranslation>["translate"];
  translationRevealed: boolean;
  onToggleTranslationReveal: (vocabularyId: number) => void;
  showMoreGrammar: boolean;
  setShowMoreGrammar: (value: boolean | ((prev: boolean) => boolean)) => void;
  onDeleteRequest: (vocabulary: DerivedVocabulary) => void;
  selected: boolean;
  onToggleSelect: (vocabularyId: number) => void;
  onPatternSignal: (signal: {
    viaCompletion: boolean;
    viaStructure: boolean;
    patternCard: ReturnType<typeof buildPatternCardFromVocabulary>;
  }) => void;
}) {
  const markVocabularyAsLearned = useMarkVocabularyAsLearned();
  const markVocabularyAsForgotten = useMarkVocabularyAsForgotten();
  const translationDetails = useMemo(
    () =>
      normalizeVocabularyTranslationDetails({
        translation: vocabulary.translation,
        translation_details: vocabulary.translation_details,
      }),
    [vocabulary.translation, vocabulary.translation_details],
  );
  const structureDetails = useMemo(
    () =>
      getVocabularyStructureDetails({
        word: vocabulary.word,
        translation: vocabulary.translation,
        translation_details: vocabulary.translation_details,
      }),
    [vocabulary.word, vocabulary.translation, vocabulary.translation_details],
  );
  const primaryTranslation = useMemo(
    () =>
      getVocabularyPrimaryTranslation({
        translation: vocabulary.translation,
        translation_details: vocabulary.translation_details,
      }),
    [vocabulary.translation, vocabulary.translation_details],
  );
  const primaryWord = useMemo(
    () =>
      getVocabularyPrimaryWord({
        word: vocabulary.word,
        translation: vocabulary.translation,
        translation_details: vocabulary.translation_details,
      }),
    [vocabulary.word, vocabulary.translation, vocabulary.translation_details],
  );
  const surfaceWord = useMemo(
    () =>
      getVocabularySurfaceWord({
        word: vocabulary.word,
        translation: vocabulary.translation,
        translation_details: vocabulary.translation_details,
      }),
    [vocabulary.word, vocabulary.translation, vocabulary.translation_details],
  );
  const baseWord = useMemo(
    () =>
      getVocabularyBaseWord({
        word: vocabulary.word,
        translation: vocabulary.translation,
        translation_details: vocabulary.translation_details,
      }),
    [vocabulary.word, vocabulary.translation, vocabulary.translation_details],
  );
  const sourceSentence = normalizeVocabularySourceSentence(vocabulary.sentence);
  const translatedSentence = translationDetails.translated_sentence;
  const primarySentence = sourceSentence;
  const secondarySentence =
    translatedSentence && translatedSentence !== primaryTranslation
      ? translatedSentence
      : null;

  const nextReviewCopy = vocabulary.nextReviewDate
    ? vocabulary.isDue
      ? translate("progress_due_now")
      : `${translate("progress_due_in")} ${formatRelativeToNow(vocabulary.nextReviewDate)}`
    : translate("progress_never");

  const lastPracticedCopy = vocabulary.last_tested
    ? new Intl.DateTimeFormat(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric",
      }).format(new Date(vocabulary.last_tested))
    : translate("progress_never");
  const addedOnCopy =
    formatVocabularyCreatedAt(vocabulary.created_at) ??
    translate("progress_never");
  const patternCard = useMemo(
    () =>
      buildPatternCardFromVocabulary({
        vocabulary,
        details: translationDetails,
        structurePattern: structureDetails.pattern_explanation,
        contextPattern: structureDetails.contextual_meaning,
      }),
    [translationDetails, vocabulary],
  );

  return (
    <tr className="hover:bg-slate-50">
      <TableCell>
        <label className="inline-flex items-center">
          <input
            type="checkbox"
            className="h-5 w-5 rounded border-slate-300"
            checked={selected}
            onChange={() => onToggleSelect(vocabulary.id)}
          />
        </label>
      </TableCell>
      <TableCell>
        <div className="flex items-center gap-2">
          <p className="font-semibold">{primaryWord}</p>
          <VocabularyPronunciationButton vocabulary={vocabulary} />
        </div>
        {surfaceWord !== primaryWord && (
          <p className="text-xs text-slate-500">
            {translate("vocabulary_label_seen_form")}: {surfaceWord}
          </p>
        )}
        <p className="text-xs text-slate-500">
          {vocabulary.word_language.replaceAll("_", " ")}
        </p>
        <p className="text-xs text-slate-500">
          {translate("vocabulary_label_added_on")}: {addedOnCopy}
        </p>
      </TableCell>
      <TableCell>
        <p className="text-xs text-slate-500">
          {translate("vocabulary_guess_prompt")}
        </p>
        <p
          className={`mt-1 text-base font-medium transition ${
            translationRevealed ? "text-slate-900" : "blur-sm select-none"
          }`}
          aria-label={
            translationRevealed
              ? translate("vocabulary_guess_translation_visible")
              : translate("vocabulary_guess_translation_hidden")
          }
        >
          {primaryTranslation}
        </p>
        <button
          type="button"
          onClick={() => onToggleTranslationReveal(vocabulary.id)}
          className="mt-2 rounded-full border border-slate-300 px-3 py-1 text-xs font-semibold text-slate-700 transition hover:border-slate-500"
        >
          {translationRevealed
            ? translate("vocabulary_guess_hide_answer")
            : translate("vocabulary_guess_reveal_answer")}
        </button>
        {baseWord && (
          <p className="mt-1 text-left text-xs text-slate-600">
            <span className="font-semibold">
              {translate("vocabulary_label_base_form")}:
            </span>{" "}
            {baseWord}
          </p>
        )}
        <p className="mt-1 text-left text-xs text-slate-700">
          {primarySentence
            ? primarySentence
            : translate("vocabulary_source_sentence_unavailable")}
        </p>
        {secondarySentence && (
          <p className="mt-1 text-left text-xs text-slate-500">{secondarySentence}</p>
        )}
        <p className="mt-2 text-[11px] uppercase tracking-wide text-slate-500">
          {translate("vocabulary_label_structure")}
        </p>
        <div className="mt-1 flex flex-wrap gap-1 text-[11px]">
          {translationDetails.grammar.part_of_speech && (
            <span className="rounded-full bg-slate-100 px-2 py-0.5 text-slate-700">
              {translate("vocabulary_label_pos")}:{" "}
              {translationDetails.grammar.part_of_speech}
            </span>
          )}
          {translationDetails.grammar.tense && (
            <span className="rounded-full bg-slate-100 px-2 py-0.5 text-slate-700">
              {translate("vocabulary_label_tense")}:{" "}
              {translationDetails.grammar.tense}
            </span>
          )}
          {translationDetails.grammar.number && (
            <span className="rounded-full bg-slate-100 px-2 py-0.5 text-slate-700">
              {translate("vocabulary_label_number")}:{" "}
              {translationDetails.grammar.number}
            </span>
          )}
          {translationDetails.grammar.gender && (
            <span className="rounded-full bg-slate-100 px-2 py-0.5 text-slate-700">
              {translate("vocabulary_label_gender")}:{" "}
              {translationDetails.grammar.gender}
            </span>
          )}
          {translationDetails.source_plural_form && (
            <span className="rounded-full bg-slate-100 px-2 py-0.5 text-slate-700">
              {translate("vocabulary_label_plural")}:{" "}
              {translationDetails.source_plural_form}
            </span>
          )}
        </div>
        {showMoreGrammar && (
          <div className="mt-2 space-y-1 text-xs text-slate-600">
            <p>
              <span className="font-semibold">
                {translate("vocabulary_label_core_idea")}:
              </span>{" "}
              {structureDetails.core_idea || "\u2014"}
            </p>
            <p>
              <span className="font-semibold">
                {translate("vocabulary_label_memory_hint")}:
              </span>{" "}
              {structureDetails.memory_hint || "\u2014"}
            </p>
            <p>
              <span className="font-semibold">
                {translate("vocabulary_label_alternatives")}:
              </span>{" "}
              {translationDetails.alternative_meanings || "\u2014"}
            </p>
            <p>
              <span className="font-semibold">
                {translate("vocabulary_label_form_note")}:
              </span>{" "}
              {structureDetails.form_note || "\u2014"}
            </p>
            <p>
              <span className="font-semibold">
                {translate("vocabulary_label_pattern")}:
              </span>{" "}
              {structureDetails.pattern_explanation || "\u2014"}
            </p>
            <p>
              <span className="font-semibold">
                {translate("vocabulary_label_etymology")}:
              </span>{" "}
              {structureDetails.etymology || "\u2014"}
            </p>
            <div>
              <p className="font-semibold">
                {translate("vocabulary_label_breakdown")}:
              </p>
              {structureDetails.example_breakdown.length > 0 ? (
                <ul className="mt-1 list-disc pl-4 space-y-1">
                  {structureDetails.example_breakdown.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              ) : (
                <p className="text-slate-400">{"\u2014"}</p>
              )}
            </div>
            <div>
              <p className="font-semibold">
                {translate("vocabulary_label_related_examples")}:
              </p>
              {structureDetails.related_examples.length > 0 ? (
                <ul className="mt-1 list-disc pl-4 space-y-1">
                  {structureDetails.related_examples.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              ) : (
                <p className="text-slate-400">{"\u2014"}</p>
              )}
            </div>
          </div>
        )}
        <div className="mt-2 flex justify-end">
          <button
            type="button"
            onClick={() => {
              const nextShowMore = !showMoreGrammar;
              if (nextShowMore) {
                onPatternSignal({
                  viaCompletion: false,
                  viaStructure: true,
                  patternCard,
                });
              }
              setShowMoreGrammar(nextShowMore);
            }}
            className="px-0 py-0 text-[11px] font-semibold text-slate-600 hover:text-blue-700 focus-visible:outline-none"
          >
            {showMoreGrammar
              ? translate("vocabulary_less_structure")
              : translate("vocabulary_more_structure")}
          </button>
        </div>
      </TableCell>
      <TableCell>
        <div className="flex flex-col gap-0.5">
          <StageBadge stage={vocabulary.stage} translate={translate} />
          {vocabulary.stage === "LEARNING" && (
            <p className="text-[11px] text-slate-500">
              {translate("vocabulary_progress_stage_learning_interval")}
            </p>
          )}
          {vocabulary.stage === "REVIEWING" && (
            <p className="text-[11px] text-slate-500">
              {translate("vocabulary_progress_stage_reviewing_interval")}
            </p>
          )}
          {vocabulary.stage === "MASTERED" && (
            <p className="text-[11px] text-slate-500">
              {translate("vocabulary_progress_stage_mastered_interval")}
            </p>
          )}
        </div>
      </TableCell>
      <TableCell>
        <div className="flex items-center gap-2">
          <span>{vocabulary.winning_streak}</span>
          <InformationCircleIcon className="h-4 w-4 text-slate-400" title={translate(streakStageKey(vocabulary.winning_streak))} />
        </div>
      </TableCell>
      <TableCell>{lastPracticedCopy}</TableCell>
      <TableCell>{nextReviewCopy}</TableCell>
      <TableCell>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() =>
              markVocabularyAsForgotten.mutateAsync({
                vocabularyId: vocabulary.id,
              }).then(() =>
                onPatternSignal({
                  viaCompletion: true,
                  viaStructure: false,
                  patternCard,
                }),
              )
            }
            className="inline-flex items-center gap-1 rounded-full border border-slate-200 px-3 py-1 text-xs text-slate-600 hover:border-rose-200 hover:text-rose-600 transition-colors"
          >
            <HandThumbDownIcon className="h-4 w-4" />
            {translate("vocabulary_progress_actions_forgotten")}
          </button>
          <button
            onClick={() =>
              markVocabularyAsLearned.mutateAsync({
                vocabularyId: vocabulary.id,
              }).then(() =>
                onPatternSignal({
                  viaCompletion: true,
                  viaStructure: false,
                  patternCard,
                }),
              )
            }
            className="inline-flex items-center gap-1 rounded-full border border-slate-200 px-3 py-1 text-xs text-slate-600 hover:border-emerald-200 hover:text-emerald-600 transition-colors"
          >
            <HandThumbUpIcon className="h-4 w-4" />
            {translate("vocabulary_progress_actions_learned")}
          </button>
          <button
            onClick={() => onDeleteRequest(vocabulary)}
            className="inline-flex items-center gap-1 rounded-full border border-slate-200 px-3 py-1 text-xs text-rose-600 hover:border-rose-300 hover:bg-rose-50 transition-colors"
          >
            <TrashIcon className="h-4 w-4" />
            {translate("vocabulary_progress_actions_delete")}
          </button>
        </div>
      </TableCell>
    </tr>
  );
}

function VocabularyCardRow({
  vocabulary,
  translate,
  translationRevealed,
  onToggleTranslationReveal,
  showMoreGrammar,
  setShowMoreGrammar,
  onDeleteRequest,
  selected,
  onToggleSelect,
  onPatternSignal,
}: {
  vocabulary: DerivedVocabulary;
  translate: ReturnType<typeof useTranslation>["translate"];
  translationRevealed: boolean;
  onToggleTranslationReveal: (vocabularyId: number) => void;
  showMoreGrammar: boolean;
  setShowMoreGrammar: (value: boolean | ((prev: boolean) => boolean)) => void;
  onDeleteRequest: (vocabulary: DerivedVocabulary) => void;
  selected: boolean;
  onToggleSelect: (vocabularyId: number) => void;
  onPatternSignal: (signal: {
    viaCompletion: boolean;
    viaStructure: boolean;
    patternCard: ReturnType<typeof buildPatternCardFromVocabulary>;
  }) => void;
}) {
  const markVocabularyAsLearned = useMarkVocabularyAsLearned();
  const markVocabularyAsForgotten = useMarkVocabularyAsForgotten();
  const translationDetails = useMemo(
    () =>
      normalizeVocabularyTranslationDetails({
        translation: vocabulary.translation,
        translation_details: vocabulary.translation_details,
      }),
    [vocabulary.translation, vocabulary.translation_details],
  );
  const structureDetails = useMemo(
    () =>
      getVocabularyStructureDetails({
        word: vocabulary.word,
        translation: vocabulary.translation,
        translation_details: vocabulary.translation_details,
      }),
    [vocabulary.word, vocabulary.translation, vocabulary.translation_details],
  );
  const primaryTranslation = useMemo(
    () =>
      getVocabularyPrimaryTranslation({
        translation: vocabulary.translation,
        translation_details: vocabulary.translation_details,
      }),
    [vocabulary.translation, vocabulary.translation_details],
  );
  const primaryWord = useMemo(
    () =>
      getVocabularyPrimaryWord({
        word: vocabulary.word,
        translation: vocabulary.translation,
        translation_details: vocabulary.translation_details,
      }),
    [vocabulary.word, vocabulary.translation, vocabulary.translation_details],
  );
  const surfaceWord = useMemo(
    () =>
      getVocabularySurfaceWord({
        word: vocabulary.word,
        translation: vocabulary.translation,
        translation_details: vocabulary.translation_details,
      }),
    [vocabulary.word, vocabulary.translation, vocabulary.translation_details],
  );
  const baseWord = useMemo(
    () =>
      getVocabularyBaseWord({
        word: vocabulary.word,
        translation: vocabulary.translation,
        translation_details: vocabulary.translation_details,
      }),
    [vocabulary.word, vocabulary.translation, vocabulary.translation_details],
  );
  const sourceSentence = normalizeVocabularySourceSentence(vocabulary.sentence);
  const translatedSentence = translationDetails.translated_sentence;
  const primarySentence = sourceSentence;
  const secondarySentence =
    translatedSentence && translatedSentence !== primaryTranslation
      ? translatedSentence
      : null;

  const nextReviewCopy = vocabulary.nextReviewDate
    ? vocabulary.isDue
      ? translate("progress_due_now")
      : `${translate("progress_due_in")} ${formatRelativeToNow(vocabulary.nextReviewDate)}`
    : translate("progress_never");
  const addedOnCopy =
    formatVocabularyCreatedAt(vocabulary.created_at) ??
    translate("progress_never");
  const patternCard = useMemo(
    () =>
      buildPatternCardFromVocabulary({
        vocabulary,
        details: translationDetails,
        structurePattern: structureDetails.pattern_explanation,
        contextPattern: structureDetails.contextual_meaning,
      }),
    [translationDetails, vocabulary],
  );

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4">
      <div className="flex items-center justify-between">
        <div className="flex items-start gap-2">
          <label className="pt-1">
            <input
              type="checkbox"
              className="h-5 w-5 rounded border-slate-300"
              checked={selected}
              onChange={() => onToggleSelect(vocabulary.id)}
            />
          </label>
          <div>
          <div className="flex items-center gap-2">
            <p className="text-lg font-semibold text-slate-900">
              {primaryWord}
            </p>
            <VocabularyPronunciationButton vocabulary={vocabulary} />
          </div>
          <p className="mt-1 text-xs text-slate-500">
            {translate("vocabulary_guess_prompt")}
          </p>
          <p className={`text-sm transition ${translationRevealed ? "text-slate-500" : "blur-sm select-none"}`}>
            {primaryTranslation}
          </p>
          <button
            type="button"
            onClick={() => onToggleTranslationReveal(vocabulary.id)}
            className="mt-2 rounded-full border border-slate-300 px-3 py-1 text-xs font-semibold text-slate-700 transition hover:border-slate-500"
          >
            {translationRevealed
              ? translate("vocabulary_guess_hide_answer")
              : translate("vocabulary_guess_reveal_answer")}
          </button>
          {baseWord && (
            <p className="mt-1 text-left text-xs text-slate-600">
              <span className="font-semibold">
                {translate("vocabulary_label_base_form")}:
              </span>{" "}
              {baseWord}
            </p>
          )}
          {surfaceWord !== primaryWord && (
            <p className="mt-1 text-left text-xs text-slate-600">
              <span className="font-semibold">
                {translate("vocabulary_label_seen_form")}:
              </span>{" "}
              {surfaceWord}
            </p>
          )}
          <p className="mt-1 text-left text-xs text-slate-500">
            {translate("vocabulary_label_added_on")}: {addedOnCopy}
          </p>
          <p className="mt-1 text-left text-xs text-slate-700">
            {primarySentence
              ? highlightVocabularyWordInSentence(primarySentence, surfaceWord)
              : translate("vocabulary_source_sentence_unavailable")}
          </p>
          {secondarySentence && (
            <p className="mt-1 text-left text-xs text-slate-500">{secondarySentence}</p>
          )}
          <p className="mt-2 text-[11px] uppercase tracking-wide text-slate-500">
            {translate("vocabulary_label_structure")}
          </p>
          <div className="mt-1 flex flex-wrap gap-1 text-[11px]">
            {translationDetails.grammar.part_of_speech && (
              <span className="rounded-full bg-slate-100 px-2 py-0.5 text-slate-700">
                {translate("vocabulary_label_pos")}:{" "}
                {translationDetails.grammar.part_of_speech}
              </span>
            )}
            {translationDetails.grammar.tense && (
              <span className="rounded-full bg-slate-100 px-2 py-0.5 text-slate-700">
                {translate("vocabulary_label_tense")}:{" "}
                {translationDetails.grammar.tense}
              </span>
            )}
            {translationDetails.grammar.number && (
              <span className="rounded-full bg-slate-100 px-2 py-0.5 text-slate-700">
                {translate("vocabulary_label_number")}:{" "}
                {translationDetails.grammar.number}
              </span>
            )}
            {translationDetails.grammar.gender && (
              <span className="rounded-full bg-slate-100 px-2 py-0.5 text-slate-700">
                {translate("vocabulary_label_gender")}:{" "}
                {translationDetails.grammar.gender}
              </span>
            )}
            {translationDetails.source_plural_form && (
              <span className="rounded-full bg-slate-100 px-2 py-0.5 text-slate-700">
                {translate("vocabulary_label_plural")}:{" "}
                {translationDetails.source_plural_form}
              </span>
            )}
          </div>
          {showMoreGrammar && (
            <div className="mt-2 space-y-1 text-xs text-slate-600">
              <p>
                <span className="font-semibold">
                  {translate("vocabulary_label_core_idea")}:
                </span>{" "}
                {structureDetails.core_idea || "\u2014"}
              </p>
              <p>
                <span className="font-semibold">
                  {translate("vocabulary_label_memory_hint")}:
                </span>{" "}
                {structureDetails.memory_hint || "\u2014"}
              </p>
              <p>
                <span className="font-semibold">
                  {translate("vocabulary_label_alternatives")}:
                </span>{" "}
                {translationDetails.alternative_meanings || "\u2014"}
              </p>
              <p>
                <span className="font-semibold">
                  {translate("vocabulary_label_form_note")}:
                </span>{" "}
                {structureDetails.form_note || "\u2014"}
              </p>
              <p>
                <span className="font-semibold">
                  {translate("vocabulary_label_pattern")}:
                </span>{" "}
                {structureDetails.pattern_explanation || "\u2014"}
              </p>
              <p>
                <span className="font-semibold">
                  {translate("vocabulary_label_etymology")}:
                </span>{" "}
                {structureDetails.etymology || "\u2014"}
              </p>
              <div>
                <p className="font-semibold">
                  {translate("vocabulary_label_breakdown")}:
                </p>
                {structureDetails.example_breakdown.length > 0 ? (
                  <ul className="mt-1 list-disc pl-4 space-y-1">
                    {structureDetails.example_breakdown.map((item) => (
                      <li key={item}>{formatBilingualExample(item)}</li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-slate-400">{"\u2014"}</p>
                )}
              </div>
              <div>
                <p className="font-semibold">
                  {translate("vocabulary_label_related_examples")}:
                </p>
                {structureDetails.related_examples.length > 0 ? (
                  <ul className="mt-1 list-disc pl-4 space-y-1">
                    {structureDetails.related_examples.map((item) => (
                      <li key={item}>{formatBilingualExample(item)}</li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-slate-400">{"\u2014"}</p>
                )}
              </div>
            </div>
          )}
          <div className="mt-2 flex justify-end">
            <button
              type="button"
              onClick={() => {
                const nextShowMore = !showMoreGrammar;
                if (nextShowMore) {
                  onPatternSignal({
                    viaCompletion: false,
                    viaStructure: true,
                    patternCard,
                  });
                }
                setShowMoreGrammar(nextShowMore);
              }}
              className="px-0 py-0 text-[11px] font-semibold text-slate-600 hover:text-blue-700 focus-visible:outline-none"
            >
              {showMoreGrammar
                ? translate("vocabulary_less_structure")
                : translate("vocabulary_more_structure")}
            </button>
          </div>
          </div>
        </div>
        <StageBadge stage={vocabulary.stage} translate={translate} />
      </div>
      <div className="mt-3 grid grid-cols-2 gap-3 text-sm text-slate-600">
        <div>
          <p className="text-xs uppercase text-slate-400">
            {translate("progress_streak_label")}
          </p>
          <p className="font-semibold">{vocabulary.winning_streak}</p>
        </div>
        <div>
          <p className="text-xs uppercase text-slate-400">
            {translate("progress_next_review")}
          </p>
          <p className="font-semibold">{nextReviewCopy}</p>
        </div>
        <div>
          <p className="text-xs uppercase text-slate-400">
            {translate("progress_last_practiced")}
          </p>
          <p className="font-semibold">
            {vocabulary.last_tested
              ? new Intl.DateTimeFormat(undefined, {
                  month: "short",
                  day: "numeric",
                }).format(new Date(vocabulary.last_tested))
              : translate("progress_never")}
          </p>
        </div>
        <div>
          <p className="text-xs uppercase text-slate-400">
            {translate("vocabulary_progress_table_language")}
          </p>
          <p className="font-semibold">
            {vocabulary.word_language.replaceAll("_", " ")}
          </p>
        </div>
      </div>
      <div className="mt-4 flex flex-wrap gap-2">
        <button
          onClick={() =>
            markVocabularyAsForgotten.mutateAsync({
              vocabularyId: vocabulary.id,
            }).then(() =>
              onPatternSignal({
                viaCompletion: true,
                viaStructure: false,
                patternCard,
              }),
            )
          }
          className="flex-1 inline-flex items-center justify-center gap-1 rounded-full border border-slate-200 px-3 py-2 text-sm text-slate-600 hover:border-rose-200 hover:text-rose-600 transition-colors"
        >
          <HandThumbDownIcon className="h-4 w-4" />
          {translate("vocabulary_progress_actions_forgotten")}
        </button>
        <button
          onClick={() =>
            markVocabularyAsLearned.mutateAsync({
              vocabularyId: vocabulary.id,
            }).then(() =>
              onPatternSignal({
                viaCompletion: true,
                viaStructure: false,
                patternCard,
              }),
            )
          }
          className="flex-1 inline-flex items-center justify-center gap-1 rounded-full border border-slate-200 px-3 py-2 text-sm text-slate-600 hover:border-emerald-200 hover:text-emerald-600 transition-colors"
        >
          <HandThumbUpIcon className="h-4 w-4" />
          {translate("vocabulary_progress_actions_learned")}
        </button>
        <button
          onClick={() => onDeleteRequest(vocabulary)}
          className="flex-1 inline-flex items-center justify-center gap-1 rounded-full border border-slate-200 px-3 py-2 text-sm text-rose-600 hover:border-rose-300 hover:bg-rose-50 transition-colors"
        >
          <TrashIcon className="h-4 w-4" />
          {translate("vocabulary_progress_actions_delete")}
        </button>
      </div>
    </div>
  );
}

function TableHeader({ children }: { children: ReactNode }) {
  return (
    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
      {children}
    </th>
  );
}

function TableCell({ children }: { children: ReactNode }) {
  return <td className="px-4 py-3 align-top">{children}</td>;
}

function StageBadge({
  stage,
  translate,
}: {
  stage: Exclude<VocabularyStageWithAll, "ALL">;
  translate: ReturnType<typeof useTranslation>["translate"];
}) {
  const colorMap: Record<Exclude<VocabularyStageWithAll, "ALL">, string> = {
    DUE: "bg-rose-100 text-rose-800",
    NEW: "bg-blue-100 text-blue-800",
    LEARNING: "bg-amber-100 text-amber-800",
    REVIEWING: "bg-indigo-100 text-indigo-800",
    MASTERED: "bg-emerald-100 text-emerald-800",
  };
  return (
    <span
      className={`inline-flex items-center justify-center rounded-full px-3 py-1 text-xs font-semibold text-center ${colorMap[stage]}`}
    >
      {translate(
        STAGE_META.find((meta) => meta.id === stage)?.labelKey ??
          "progress_stage_new",
      )}
    </span>
  );
}

function DeleteVocabularyDialog({
  vocabularies,
  mode,
  translate,
  isLoading,
  onCancel,
  onConfirm,
}: {
  vocabularies: DerivedVocabulary[];
  mode: "single" | "multi";
  translate: ReturnType<typeof useTranslation>["translate"];
  isLoading: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  const singleVocabulary = vocabularies[0] ?? null;
  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-slate-900/40 px-4 py-8">
      <div className="w-full max-w-md rounded-2xl bg-white p-6">
        <h3 className="text-lg font-semibold text-slate-900">
          {translate("vocabulary_progress_delete_title")}
        </h3>
        <p className="mt-2 text-sm text-slate-600">
          {mode === "single" && singleVocabulary
            ? translate("vocabulary_progress_delete_message", {
                word: singleVocabulary.word,
              })
            : translate("vocabulary_progress_delete_multi_message", {
                count: vocabularies.length,
              })}
        </p>
        <div className="mt-6 flex justify-end gap-3">
          <button
            type="button"
            className="rounded-full border border-slate-200 px-4 py-2 text-sm font-medium text-slate-600 hover:border-slate-400 hover:text-slate-900 transition-colors disabled:opacity-50"
            onClick={onCancel}
            disabled={isLoading}
          >
            {translate("vocabulary_progress_delete_cancel")}
          </button>
          <button
            type="button"
            className="rounded-full bg-rose-600 px-4 py-2 text-sm font-semibold text-white hover:bg-rose-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            onClick={onConfirm}
            disabled={isLoading}
          >
            {isLoading
              ? translate("vocabulary_progress_delete_pending")
              : translate("vocabulary_progress_delete_confirm")}
          </button>
        </div>
      </div>
    </div>
  );
}

function FilterChip({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className={`rounded-full border px-3 py-1 text-sm transition-colors ${
        active
          ? "border-slate-900 bg-slate-900 text-white"
          : "border-slate-200 text-slate-600 hover:border-slate-900 hover:text-slate-900"
      }`}
      type="button"
    >
      {label}
    </button>
  );
}

function SrsInfoPanel({
  translate,
}: {
  translate: ReturnType<typeof useTranslation>["translate"];
}) {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4">
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        className="flex w-full items-center justify-between text-left"
      >
        <div>
          <p className="text-sm font-semibold uppercase tracking-wide text-slate-500">
            {translate("vocabulary_progress_srs_heading")}
          </p>
          <p className="text-base font-semibold text-slate-900">
            {translate("vocabulary_progress_srs_title")}
          </p>
        </div>
        <div className="flex items-center gap-1 rounded-full border border-slate-300 px-3 py-1 text-xs text-slate-600">
          <InformationCircleIcon className="h-4 w-4" />
          {translate(
            open
              ? "vocabulary_progress_srs_toggle_hide"
              : "vocabulary_progress_srs_toggle_show",
          )}
        </div>
      </button>
      {open && (
        <div className="mt-4 space-y-4 text-sm text-slate-600">
          <p>{translate("vocabulary_progress_srs_intro")}</p>
          <div>
            <p className="font-semibold text-slate-800">
              {translate("vocabulary_progress_srs_actions_heading")}
            </p>
            <ul className="mt-1 list-disc space-y-1 pl-4">
              <li>{translate("vocabulary_progress_srs_action_known")}</li>
              <li>{translate("vocabulary_progress_srs_action_almost")}</li>
              <li>{translate("vocabulary_progress_srs_action_unknown")}</li>
            </ul>
          </div>
          <div>
            <p className="font-semibold text-slate-800">
              {translate("vocabulary_progress_srs_schedule_heading")}
            </p>
            <dl className="mt-2 grid gap-3 sm:grid-cols-2">
              {SRS_SCHEDULE.map((entry) => (
                <div
                  key={entry.streakLabel}
                  className="rounded-xl border border-slate-100 bg-slate-50 px-3 py-2"
                >
                  <dt className="text-xs uppercase tracking-wide text-slate-500">
                    {translate("vocabulary_progress_srs_streak_label", {
                      streak: entry.streakLabel,
                    })}
                  </dt>
                  <dd className="text-sm font-semibold text-slate-800">
                    {translate(entry.translationKey)}
                  </dd>
                </div>
              ))}
            </dl>
          </div>
          <p className="text-xs text-slate-500">
            {translate("vocabulary_progress_srs_due_hint")}
          </p>
        </div>
      )}
    </div>
  );
}
